import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Rating,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import { ExpandMore, StarBorder, FitnessCenter, FlagOutlined } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import ImageViewModal from 'components/shared/modals/ImageViewModal';
import GradeResultDialog from 'components/play/GradeResultDialog';
import { CLIENT_PLAY_RESULTS } from 'api/graphql/play';
import { metricLabel, muscleLabel } from 'components/play/constants';

const fmtDate = (iso) => {
  try {
    return format(parseISO(iso), 'd MMM yyyy', { locale: es });
  } catch {
    return '';
  }
};

/**
 * OTRIX Play (entrenador/admin) — todos los resultados auto-reportados de un cliente,
 * agrupados por ejercicio, con acción de calificar cada uno (C6).
 */
function AdminClientPlayResults({ clientId }) {
  const { t } = useTranslation(['play']);
  const [zoom, setZoom] = useState(null);
  const [gradeTarget, setGradeTarget] = useState(null);

  const { data, loading } = useQuery(CLIENT_PLAY_RESULTS, {
    variables: { user_id: clientId },
    skip: !clientId,
    fetchPolicy: 'cache-and-network'
  });
  const results = useMemo(() => data?.clientPlayResults || [], [data]);

  const groups = useMemo(() => {
    const map = new Map();
    results.forEach((r) => {
      const ex = r.exercise;
      if (!ex) return;
      if (!map.has(ex.id)) map.set(ex.id, { exercise: ex, items: [] });
      map.get(ex.id).items.push(r);
    });
    return Array.from(map.values());
  }, [results]);

  const refetch = [{ query: CLIENT_PLAY_RESULTS, variables: { user_id: clientId } }];

  if (loading && results.length === 0) {
    return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress color="secondary" /></Stack>;
  }

  return (
    <Container maxWidth="xl" sx={{ pb: 3 }}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
        {t('adminResults.title', { ns: 'play', defaultValue: 'Resultados OTRIX Play' })}
      </Typography>

      {groups.length === 0 ? (
        <Box sx={{ p: { xs: 4, md: 6 }, textAlign: 'center', backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: 3 }}>
          <FitnessCenter sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" fontWeight={700}>
            {t('adminResults.empty', { ns: 'play', defaultValue: 'Este cliente aún no ha registrado resultados.' })}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {groups.map((group) => (
            <Accordion
              key={group.exercise.id}
              disableGutters
              sx={{ border: '1px solid', borderColor: 'hairline.main', borderRadius: 3, backgroundColor: 'surface.content', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>{group.exercise.localized_name}</Typography>
                  {group.exercise.muscle_group && (
                    <Chip size="small" label={muscleLabel(t, group.exercise.muscle_group)} sx={{ bgcolor: 'gold.soft', color: 'gold.main' }} />
                  )}
                  <Chip size="small" variant="outlined" label={group.items.length} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack divider={<Divider flexItem />} spacing={1.5}>
                  {group.items.map((r) => (
                    <Stack key={r.id} direction="row" spacing={1.5} alignItems="flex-start">
                      {r.photo_url && (
                        <Box
                          component="img"
                          src={r.photo_url}
                          alt=""
                          onClick={() => setZoom(r.photo_url)}
                          sx={{ width: 56, height: 56, borderRadius: 2, objectFit: 'cover', border: '1px solid', borderColor: 'hairline.main', cursor: 'zoom-in', flex: 'none' }}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                          <Typography variant="body1" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.value}</Typography>
                          <Typography variant="caption" color="text.secondary">{metricLabel(t, r.metric)}</Typography>
                          <Typography variant="caption" color="text.secondary">· {fmtDate(r.created_at)}</Typography>
                          {r.is_flagged && (
                            <Chip size="small" icon={<FlagOutlined sx={{ fontSize: 14 }} />} label={t('ranking.inReview', { ns: 'play', defaultValue: 'En revisión' })} sx={{ bgcolor: 'gold.soft', color: 'gold.main' }} />
                          )}
                        </Stack>
                        {r.rating ? (
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                            <Rating value={r.rating} max={5} readOnly size="small" sx={{ color: 'gold.main' }} />
                            {r.feedback && <Typography variant="caption" color="text.secondary" noWrap>· {r.feedback}</Typography>}
                          </Stack>
                        ) : null}
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<StarBorder />}
                        onClick={() => setGradeTarget(r)}
                        sx={{ flex: 'none', color: 'gold.main', borderColor: 'gold.main', textTransform: 'none', fontWeight: 700 }}>
                        {r.rating ? t('adminResults.regrade', { ns: 'play', defaultValue: 'Editar' }) : t('moderation.grade', { ns: 'play', defaultValue: 'Calificar' })}
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}

      <ImageViewModal open={Boolean(zoom)} src={zoom} alt={t('result.evidenceAlt', { ns: 'play', defaultValue: 'Evidencia' })} onClose={() => setZoom(null)} />
      <GradeResultDialog open={Boolean(gradeTarget)} result={gradeTarget} refetchQueries={refetch} onClose={() => setGradeTarget(null)} />
    </Container>
  );
}

AdminClientPlayResults.propTypes = {
  clientId: PropTypes.string.isRequired
};

export default AdminClientPlayResults;
